import { useState, useEffect, useRef, useCallback } from "react";

export const useMeditationAudio = (initialVolume = 80) => {
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [voiceVolume, setVoiceVolume] = useState(initialVolume);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (audio.duration) {
        setAudioProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      setAudioPlaying(false);
      setAudioProgress(0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = voiceVolume / 100;
    }
  }, [voiceVolume]);

  const togglePlayback = useCallback(() => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setAudioPlaying(!audioPlaying);
    }
  }, [audioPlaying]);

  // Allow setting initial volume after mount (for lesson defaults)
  const setInitialVolume = useCallback((vol) => {
    setVoiceVolume(vol);
  }, []);

  return {
    audioRef,
    audioPlaying,
    audioProgress,
    voiceVolume,
    setVoiceVolume,
    setInitialVolume,
    togglePlayback,
  };
};

export const useAmbientAudio = (initialVolume = 30) => {
  const ambientRef = useRef(null);
  const [activeAmbient, setActiveAmbient] = useState(null);
  const [ambientVolume, setAmbientVolume] = useState(initialVolume);
  const [ambientPlaying, setAmbientPlaying] = useState(false);

  useEffect(() => {
    if (ambientRef.current) {
      ambientRef.current.volume = ambientVolume / 100;
    }
  }, [ambientVolume]);

  useEffect(() => {
    return () => {
      if (ambientRef.current) {
        ambientRef.current.pause();
      }
    };
  }, []);

  const selectSound = useCallback((soundKey, soundUrl) => {
    if (activeAmbient === soundKey) {
      if (ambientRef.current) {
        ambientRef.current.pause();
      }
      setActiveAmbient(null);
      setAmbientPlaying(false);
    } else {
      setActiveAmbient(soundKey);
      if (soundUrl && ambientRef.current) {
        ambientRef.current.src = soundUrl;
        ambientRef.current.loop = true;
        ambientRef.current.volume = ambientVolume / 100;
        ambientRef.current.play();
        setAmbientPlaying(true);
      }
    }
  }, [activeAmbient, ambientVolume]);

  const togglePlayback = useCallback(() => {
    if (ambientRef.current && activeAmbient) {
      if (ambientPlaying) {
        ambientRef.current.pause();
      } else {
        ambientRef.current.play();
      }
      setAmbientPlaying(!ambientPlaying);
    }
  }, [activeAmbient, ambientPlaying]);

  // Allow setting initial volume after mount (for lesson defaults)
  const setInitialVolume = useCallback((vol) => {
    setAmbientVolume(vol);
  }, []);

  // Allow auto-selecting a default sound
  const setDefaultSound = useCallback((soundKey, soundUrl, autoPlay = false) => {
    if (soundKey && soundKey !== "none" && soundUrl) {
      setActiveAmbient(soundKey);
      if (ambientRef.current) {
        ambientRef.current.src = soundUrl;
        ambientRef.current.loop = true;
        // Don't auto-play by default, just pre-select
        if (autoPlay) {
          ambientRef.current.play();
          setAmbientPlaying(true);
        }
      }
    }
  }, []);

  return {
    ambientRef,
    activeAmbient,
    ambientVolume,
    setAmbientVolume,
    setInitialVolume,
    ambientPlaying,
    selectSound,
    togglePlayback,
    setDefaultSound,
  };
};
